import { filter, get } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "shared/hooks/useApi";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import { AUTHORITY_TYPE, GENDER, KEYS, RELATIONSHIP } from "utils/constants";
import {
  normalizeMiltisChildRelatedPerson,
  resolveChildSocialRegimeForPricing,
} from "utils/helpers/miltisRelatedPersonChild";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const RelatedPersons = ({
  project,
  onNext,
  onBack,
  currentStatus,
  tracer,
  updateUrl,
  territorial = false,
}) => {
  const [initialValues, setInitialValues] = useState({
    children: [],
    spouse: undefined,
  });

  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription");

  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const normalizePerson = (person, isSpouse = false) => {
    if (!person?.identity) return person;
    const user = person.identity?.user ?? {};
    const base = {
      ...person,
      identity: {
        ...person.identity,
        socialSecurityNumber: person.identity?.socialSecurityNumber ?? "",
        user: {
          id: user.id ?? "",
          firstname: user.firstname ?? "",
          lastname: user.lastname ?? "",
          gender: user.gender ?? GENDER.MALE,
          birthDate: user.birthDate ?? "",
        },
      },
    };
    if (isSpouse) {
      return { ...base, authority: AUTHORITY_TYPE.SELF };
    }
    return normalizeMiltisChildRelatedPerson(base);
  };

  useEffect(() => {
    const contact =
      get(project, "contact") || get(data, "subscription.project.contact");
    const relatedPersons = get(contact, "relatedPersons", []);
    const children = filter(relatedPersons, {
      relationship: RELATIONSHIP.CHILD,
    }).map((p) => {
      const person = normalizePerson(p);
      return person;
    });
    const spouseRaw = relatedPersons.find(
      (p) => p.relationship === RELATIONSHIP.SPOUSE,
    );
    const spouse = spouseRaw ? normalizePerson(spouseRaw, true) : undefined;

    setInitialValues({ children, spouse });
  }, [project, data]);

  const contact = useMemo(
    () => get(project, "contact") || get(data, "subscription.project.contact"),
    [project, data],
  );
  const adherentSocialSecurityNumber = get(contact, "socialSecurityNumber", "");
  const adherentSocialRegime = get(contact, "socialRegime", "");

  const onSubmit = ({ spouse, children }) => {
    const source = localStorage.getItem(KEYS.SOURCE);

    const normalizedSpouse = spouse ? normalizePerson(spouse, true) : undefined;
    const spouseSocialRegime = get(
      normalizedSpouse,
      "identity.socialRegime",
      "",
    );
    const normalizedChildren = children.map((person) => {
      const normalized = normalizePerson(person);
      return {
        ...normalized,
        identity: {
          ...get(normalized, "identity", {}),
          socialRegime: resolveChildSocialRegimeForPricing({
            childSocialRegime: get(normalized, "identity.socialRegime"),
            adherentSocialRegime,
            spouseSocialRegime,
            hasSpouse: !!normalizedSpouse,
          }),
        },
      };
    });

    const relatedPersons = [
      ...(normalizedSpouse ? [normalizedSpouse] : []),
      ...normalizedChildren,
    ];

    setInitialValues({
      children: normalizedChildren,
      spouse: normalizedSpouse,
    });

    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });

    const contact =
      get(project, "contact") || get(data, "subscription.project.contact");

    return onNext({
      payload: {
        contact: {
          ...contact,
          relatedPersons,
        },
      },
    });
  };
  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showCart
        showProfile
        step={4}
        stepCount={5}
        title="Mes informations contractuelles"
      />
      <View
        key={JSON.stringify(initialValues)}
        children={initialValues.children}
        spouse={initialValues.spouse}
        adherentSocialSecurityNumber={adherentSocialSecurityNumber}
        adherentSocialRegime={adherentSocialRegime}
        onNext={onSubmit}
        onBack={onBack}
        territorial={territorial}
      />
    </>
  );
};

export default RelatedPersons;
